import styled from "styled-components"
import { pagePaddingCssRules } from "../../../shared_components/Header/styles"
import { useHistory } from "react-router-dom"
import { Box } from "../../../shared_components/UI/Box"
import { useMemecoinDetails } from "../../Memecoins/TokenPage"
import { H1, P, Strong } from "../../../shared_components/Library/Typeography"
import { getLatestMemecoinPriceInEth } from "../../../utils"
import { useSelector } from "react-redux"
import { selectEthUsdPrice } from "../../../modules/web3/selectors"
import { useMemo } from "react"
import routes from "../../../router/routes"
import { useGetMemecoinUserTransactionsQuery } from "../../../modules/memecoins/api"
import AnimatedLoader from "../../../shared_components/AnimatedLoader"
import { format } from "date-fns"
import { showDecimalsIfNeeded } from "../Allocations/InvestmentInfo"
import { formatNumberToFirstDecimalDigit, getTransactionTokenAmount } from "../../Memecoins/TokenPage/helpers"
import { useShouldShowForThreshold } from "../../../theme"
import { BackButton } from "../../../shared_components/Page"

function TokenDetails(props: { match: { params: { address: string } } }) {
  const { address } = props.match.params

  const { memecoin, isLoading } = useMemecoinDetails(address)

  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('compact')

  const ethPriceInUsd = useSelector(selectEthUsdPrice)
  
  const tokenPrice = useMemo(() => {
    if (!memecoin) return 0
    
    const tokenPriceInEth = getLatestMemecoinPriceInEth(
      memecoin.latestTransaction,
      memecoin.decimals ?? 6,
    )

    if (!tokenPriceInEth) return 0
    
    return tokenPriceInEth * (ethPriceInUsd ?? 0)
    
  }, [memecoin, ethPriceInUsd])
  
  return (
    <Box dc g={isAtLeastMediumDisplaySize ? 32 : 16}>
      {isAtLeastMediumDisplaySize && <BackButton padT={0} />}
      <Header memecoin={memecoin} price={tokenPrice} />
      <TransactionsTable memecoin={memecoin} price={tokenPrice} />
    </Box>
  )
}

const TokenHeaderDetails = ({ memecoin, price }) => {
  const logoUrl = memecoin?.media?.logo?.url || '/meme-icon.png'

  const tokenPriceToDisplay = price
    ? formatNumberToFirstDecimalDigit(
      price.toLocaleString('en-GB', {
        minimumFractionDigits: 8,
        maximumFractionDigits: 8,
      })
    )
    : '--'
  
  return (
    <TokenDetailsContainer ac g={8}>
      <TokenLogo src={logoUrl} alt="coin logo" />
      <Box g={8} dc>
        <H1 s="2XL" fw="bolder" uc>{memecoin?.tokenName}</H1>
        <P s="L" c="text-secondary">{`$${tokenPriceToDisplay}`}</P>
      </Box>
    </TokenDetailsContainer>
  )
}

const Header = ({ memecoin, price }: { memecoin: any, price: any }) => {
  const { push } = useHistory()

  const handleOnClick = () => {
    push(`${routes.memecoins.index}/${memecoin?.poolAddress}`)
  }
  
  return (
    <Box g={8} jsb ac>
      <TokenHeaderDetails memecoin={memecoin} price={price} />
      <LinkButton
        onClick={handleOnClick}
      >
        <img src="/img/icons/buttons/arrow-right.svg" alt="back" />
      </LinkButton>
    </Box>
  )
}

const TransactionsTable = ({ memecoin, price }) => {
  const { data, isLoading } = useGetMemecoinUserTransactionsQuery({
    address: memecoin?.poolAddress,
  }, {
    skip: !memecoin?.poolAddress,
  })

  if (isLoading) {
    return (
      <Box c ac>
        <AnimatedLoader onlyWhite size={32} />
      </Box>
    )
  }

  return (
    <Table>
      <tbody>
        {!!data?.transactions?.length && data?.transactions?.map((transaction) => (
          <TableBodyRow key={transaction._id}>
            <td>
              <Box ac g={8}>
                <img src={`/img/icons/memecoins/table/${transaction?.event.toLowerCase()}.svg`} alt={transaction?.event} />
                <Box dc>
                  <Strong s="L">{transaction?.event === 'Purchase' ? 'Buy' : 'Sell'}</Strong>
                  <P s="S" c="text-secondary">
                    {format(new Date(transaction.timestamp), 'MMM dd,yyyy HH:mm')}
                  </P>
                </Box>
              </Box>
            </td>
            <td>
              <Box dc align="flex-end">
                <BalanceContainer>
                  <Strong uc s="L">
                    {transaction.event === 'Sell' && '-'}
                    {showDecimalsIfNeeded(
                      Number(
                        getTransactionTokenAmount(transaction, memecoin.decimals),
                      ),
                    )}
                  </Strong>
                  <P s="S">{memecoin?.tokenSymbol}</P>
                </BalanceContainer>
                <P s="S" c="text-secondary">
                  {`$${
                    formatNumberToFirstDecimalDigit(getTransactionPrice(transaction, price))
                  }`}
                </P>
              </Box>
            </td>
          </TableBodyRow>
        ))}
      </tbody>
    </Table>
  )
}

const getTransactionPrice = (transaction, price) => {
  const amount = getTransactionTokenAmount(transaction, 6)
  console.log({ amount, transaction, price, test: formatNumberToFirstDecimalDigit((Number(amount) * price).toLocaleString('en-GB', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  })) })
  return Number(amount) * price
}

const LinkButton = styled.div`
  display: flex;
  width: var(--units-40, 40px);
  height: var(--units-40, 40px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 84px;
  border: 1px solid var(--stroke-secondary, #333);
`

const TokenDetailsContainer = styled(Box)`
  align-items: center;
  flex-shrink: 0;
  height: 92px;
  align-self: stretch;
`

const TokenLogo = styled.img`
  width: 68px;
  height: 68px;

  border-radius: var(--units-88, 88px);
  border: 1px solid var(--stroke-secondary, #333);
  background: url(<path-to-image>) lightgray 50% / cover no-repeat, var(--surface-primary, #080808);
`

const Container = styled.div`
  ${pagePaddingCssRules}
  padding-top: 24px !important;
`

const Table = styled.table`
  width: 100%;
`

const BalanceContainer = styled.td`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  white-space: nowrap;
  
  ${P} {
    margin-left: 4px; // Add a bit of space between the number and symbol if needed
  }
`;

const TableRow = styled.tr`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--units-8, 8px);
  flex: 1 0 0;
  
  th:not(:first-child) {
    align-self: flex-end;
    text-align: right;
  }
`

const TableBodyRow = styled(TableRow)`
  padding: var(--units-16, 16px) 0;
  border-bottom: 1px solid var(--stroke-tertiary, #252525);

  &:first-child {
    padding-top: 0;
  }
  
  &:last-child {
    border-bottom: none;
  }
`

export default TokenDetails
